import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type AuthButtonVariant = 'primary' | 'ghost';

export interface AuthButtonProps {
  variant?: AuthButtonVariant;
  label?: ReactNode;
  children?: ReactNode;
  icon?: LucideIcon;
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
  /** Full-width by default (card CTAs); set false for inline actions. */
  fullWidth?: boolean;
  onClick?: () => void;
  className?: string;
}

/**
 * Auth primary/ghost button — reuses the shared <Button> (gradient
 * `btn-primary-anaka` for primary, `tertiary` for ghost) at the auth
 * surface's 46px / 11px-radius spec. Loading uses the reduced-motion-safe
 * `.anaka-btn-spinner`.
 */
export function AuthButton({
  variant = 'primary',
  label,
  children,
  icon: Icon,
  loading = false,
  disabled = false,
  type = 'button',
  fullWidth = true,
  onClick,
  className,
}: AuthButtonProps) {
  return (
    <Button
      type={type}
      variant={variant === 'primary' ? 'primary' : 'tertiary'}
      size="lg"
      fullWidth={fullWidth}
      disabled={disabled || loading}
      onClick={onClick}
      className={cn('anaka-auth-btn h-[46px] gap-[9px] rounded-[11px] text-[14.5px] font-semibold', className)}
    >
      {loading ? (
        <span className="anaka-btn-spinner" aria-hidden="true" />
      ) : Icon ? (
        <Icon className="size-[17px]" strokeWidth={1.9} aria-hidden="true" />
      ) : null}
      {label ?? children}
    </Button>
  );
}
